import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { inquiryService } from '../services/api';

export default function InquiriesPage() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadInquiries();
  }, []);

  const loadInquiries = async () => {
    try {
      const data = await inquiryService.getAll();
      setInquiries(data);
    } catch (error) {
      console.error('Error loading inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: 'badge-info',
      contacted: 'badge-warning',
      qualified: 'badge-success',
      closed: 'badge-gray',
    };
    return badges[status] || 'badge-gray';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Calculate stats
  const stats = {
    total: inquiries.length,
    new: inquiries.filter(i => i.status === 'new').length,
    contacted: inquiries.filter(i => i.status === 'contacted').length,
    qualified: inquiries.filter(i => i.status === 'qualified').length,
    closed: inquiries.filter(i => i.status === 'closed').length,
  };

  // Filter inquiries
  const filteredInquiries = filterStatus === 'all' 
    ? inquiries 
    : inquiries.filter(i => i.status === filterStatus);

  // Sort by newest first
  const sortedInquiries = [...filteredInquiries].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Inquiries | CR Hood Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Inquiries</h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <button
            onClick={() => setFilterStatus('all')}
            className={`p-4 rounded-xl text-center transition-all ${
              filterStatus === 'all' 
                ? 'bg-accent text-white shadow-lg' 
                : 'bg-white hover:bg-gray-50 shadow'
            }`}
          >
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className={`text-sm ${filterStatus === 'all' ? 'text-white/80' : 'text-gray-500'}`}>
              Total
            </div>
          </button>
          
          <button
            onClick={() => setFilterStatus('new')}
            className={`p-4 rounded-xl text-center transition-all ${
              filterStatus === 'new' 
                ? 'bg-blue-500 text-white shadow-lg' 
                : 'bg-white hover:bg-gray-50 shadow'
            }`}
          >
            <div className="text-2xl font-bold">{stats.new}</div>
            <div className={`text-sm ${filterStatus === 'new' ? 'text-white/80' : 'text-gray-500'}`}>
              New
            </div>
          </button>
          
          <button
            onClick={() => setFilterStatus('contacted')}
            className={`p-4 rounded-xl text-center transition-all ${
              filterStatus === 'contacted' 
                ? 'bg-yellow-500 text-white shadow-lg' 
                : 'bg-white hover:bg-gray-50 shadow'
            }`}
          >
            <div className="text-2xl font-bold">{stats.contacted}</div>
            <div className={`text-sm ${filterStatus === 'contacted' ? 'text-white/80' : 'text-gray-500'}`}>
              Contacted
            </div>
          </button>
          
          <button
            onClick={() => setFilterStatus('qualified')}
            className={`p-4 rounded-xl text-center transition-all ${
              filterStatus === 'qualified' 
                ? 'bg-green-500 text-white shadow-lg' 
                : 'bg-white hover:bg-gray-50 shadow'
            }`}
          >
            <div className="text-2xl font-bold">{stats.qualified}</div>
            <div className={`text-sm ${filterStatus === 'qualified' ? 'text-white/80' : 'text-gray-500'}`}>
              Qualified
            </div>
          </button>
          
          <button
            onClick={() => setFilterStatus('closed')}
            className={`p-4 rounded-xl text-center transition-all ${
              filterStatus === 'closed' 
                ? 'bg-gray-500 text-white shadow-lg' 
                : 'bg-white hover:bg-gray-50 shadow'
            }`}
          >
            <div className="text-2xl font-bold">{stats.closed}</div>
            <div className={`text-sm ${filterStatus === 'closed' ? 'text-white/80' : 'text-gray-500'}`}>
              Closed
            </div>
          </button>
        </div>

        {/* Inquiries List */}
        <div className="card p-0 overflow-hidden">
          {sortedInquiries.length === 0 ? (
            <div className="text-center py-12">
              {filterStatus === 'all' ? (
                <>
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p className="text-gray-500 font-medium">No inquiries yet</p>
                  <p className="text-sm text-gray-400 mt-1">Inquiries from the contact form will appear here</p>
                </>
              ) : (
                <>
                  <p className="text-gray-500">No {filterStatus} inquiries</p>
                  <button 
                    onClick={() => setFilterStatus('all')}
                    className="text-accent hover:underline text-sm mt-2"
                  >
                    View all inquiries
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Status</th>
                    <th>Received</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedInquiries.map((inquiry) => (
                    <tr key={inquiry.id}>
                      <td>
                        <Link 
                          to={`/inquiries/${inquiry.id}`}
                          className="font-medium text-gray-900 hover:text-accent"
                        >
                          {inquiry.name}
                        </Link>
                        {inquiry.status === 'new' && (
                          <span className="ml-2 inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                        )}
                      </td>
                      <td>
                        <a 
                          href={`mailto:${inquiry.email}`}
                          className="text-gray-600 hover:text-accent"
                        >
                          {inquiry.email}
                        </a>
                      </td>
                      <td className="text-gray-600">{inquiry.company || '—'}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(inquiry.status)}`}>
                          {inquiry.status}
                        </span>
                      </td>
                      <td className="text-gray-500 text-sm">
                        {formatDate(inquiry.createdAt)}
                      </td>
                      <td>
                        <Link
                          to={`/inquiries/${inquiry.id}`}
                          className="text-accent hover:underline text-sm"
                        >
                          View →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
